import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import api from "../api/api";
import QuestionRenderer from "../components/QuestionRenderer";
import "./survey.css";
import "./loader.css";

const SurveyPage = () => {
  const { surveyId: token } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  const [realSurveyId, setSurveyId] = useState(null);
  const [companyFk, setCompanyFk] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate("/expired", { replace: true });
      return;
    }
    try {
      const decodedString = decodeURIComponent(atob(token));
      const decoded = JSON.parse(decodedString);

      // ✅ Check if the token has a 24-hour expiration embedded
      if (decoded.expiresAt && Date.now() > decoded.expiresAt) {
        console.warn("⏳ Survey link has expired.");
        localStorage.setItem("surveyErrorMsg", "This individual survey link has expired. Please request a new link.");
        navigate("/expired", { replace: true });
        return;
      }

      setSurveyId(decoded.surveyId);
      setCompanyFk(decoded.company_fk);
    } catch (err) {
      console.error("❌ Invalid survey token:", err);
      navigate("/expired", { replace: true });
    }
  }, [token, navigate]);

  // ✅ STATE
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [currentQuestionPage, setCurrentQuestionPage] = useState(0);

  const [departments, setDepartments] = useState([]);
  const [levels, setLevels] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [location, setLocation] = useState("");
  const [locationError, setLocationError] = useState("");

  const [companyText, setCompanyText] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [departmentText, setDepartmentText] = useState("");
  const [departmentError, setDepartmentError] = useState("");
  const [levelText, setLevelText] = useState("");
  const [levelError, setLevelError] = useState("");

  // ✅ Field validators — enforce that respondents type in real details,
  // not stray characters or a single word/number.
  const isValidFullName = (val) => {
    const trimmed = String(val || "").trim();
    if (trimmed.length < 2) return false;
    if (!/^[A-Za-z\s.'-]+$/.test(trimmed)) return false; // letters only
    return trimmed.split(/\s+/).filter(Boolean).length >= 2; // first + last name
  };
  const isValidPhoneNumber = (val) => /^[0-9]{10,12}$/.test(String(val || "").trim());
  const isValidFreeText = (val) => {
    const trimmed = String(val || "").trim();
    if (trimmed.length < 2) return false;
    if (/^\d+$/.test(trimmed)) return false; // reject pure numbers
    return true;
  };

  const [instructionsRead, setInstructionsRead] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);

  const QUESTIONS_PER_PAGE = 5;

  const stripOptionLabel = (opt) => opt.replace(/^[A-Z]\.\s*/, "");

  // ✅ Johari Window (survey_type_fk = 7): extract the leading letter ("A", "B", "C"...)
  // from an mcq_option string like "A. I openly share my feelings"
  const getOptionLetter = (opt) => {
    const match = String(opt ?? "").match(/^([A-Z])\./);
    return match ? match[1] : String(opt ?? "");
  };

  useEffect(() => {
    if (!realSurveyId || companyFk === null || companyFk === undefined) return;

    const fetchSurvey = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/question/${realSurveyId}`, {
          params: { company_id: companyFk }
        });
        if (res.data?.expired === true) {
          localStorage.setItem("surveyErrorMsg", res.data?.error || "Survey expired");
          navigate("/expired", { replace: true });
          return;
        }
        setSurvey(res.data || { groups: [] });
      } catch (error) {
        console.error("Load failed:", error);
        navigate("/expired", { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchSurvey();
  }, [realSurveyId, companyFk, navigate]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        if (companyFk === null || companyFk === undefined || String(companyFk) === "0") return;

        const [deptRes, levelRes] = await Promise.all([
          api.get(`/departments?company_fk=${companyFk}`),
          api.get(`/levels?company_fk=${companyFk}`),
        ]);
        setDepartments(deptRes.data || []);
        setLevels(levelRes.data || []);
      } catch (err) {
        console.error("Dropdown fetch error:", err);
      }
    };
    fetchDropdownData();
  }, [companyFk]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentGroupIndex, currentQuestionPage, instructionsRead]);

  const handleAnswerChange = (questionId, value, checked = null) => {
    setAnswers((prev) => {
      const updated = { ...prev };
      if (checked !== null) {
        updated[questionId] = checked
          ? [...(updated[questionId] || []), value]
          : updated[questionId].filter((v) => v !== value);
      } else {
        updated[questionId] = value;
      }
      return updated;
    });
  };

  // ✅ Johari Window (survey_type_fk = 7): handler for the paired 0–5 score dropdowns.
  // Answer shape stored per question: { [letterA]: valueA, [letterB]: valueB } where valueA + valueB === 5.
  const handleJHScoreChange = (questionId, letterA, letterB, changedLetter, rawValue) => {
    setAnswers((prev) => {
      if (rawValue === "") {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      }
      const value = Number(rawValue);
      const complement = 5 - value;
      const next =
        changedLetter === letterA
          ? { [letterA]: value, [letterB]: complement }
          : { [letterB]: value, [letterA]: complement };
      return { ...prev, [questionId]: next };
    });
  };

  // Handler for Navigating Conflict questions (selected_answer + who_do_this)
  const handleNavigatingConflictChange = (questionId, field, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || { selected_answer: null, who_do_this: false }),
        [field]: value,
      },
    }));
  };

  // const isType3 = String(survey?.survey_type_fk) === "3";
  const isType2 = String(survey?.survey_type_fk) === "2";
  const isType3 = String(survey?.survey_type_fk) === "3";
  const isType4 = String(survey?.survey_type_fk) === "4";
  const isType5 = String(survey?.survey_type_fk) === "5";
  const isType6 = String(survey?.survey_type_fk) === "6";
  const isType7 = String(survey?.survey_type_fk) === "7";
  const isType8 = String(survey?.survey_type_fk) === "8";
  const isType9 = String(survey?.survey_type_fk) === "9";
  const isType10 = String(survey?.survey_type_fk) === "10";
  const isType11 = String(survey?.survey_type_fk) === "11";
  const isType12 = String(survey?.survey_type_fk) === "12";
  // -- For survey_type_fk = 2: render regular questions first, team-culture questions after --
  const isTeamCultureGroup = (group) =>
    group.questions?.some((q) => q.is_team_culture === true);

  const regularGroups = (survey?.groups || []).filter((g) => !isTeamCultureGroup(g));
  const teamCultureGroups = (survey?.groups || []).filter((g) => isTeamCultureGroup(g));
  const reorderedGroups = isType2
    ? [...regularGroups, ...teamCultureGroups]
    : (survey?.groups || []);

  const currentGroup = {
    ...reorderedGroups[currentGroupIndex],
    questions:
      reorderedGroups[currentGroupIndex]?.questions?.slice().sort((a, b) => {
        if (a.ans_type === "dual_textbox" && b.ans_type !== "dual_textbox") return 1;
        if (b.ans_type === "dual_textbox" && a.ans_type !== "dual_textbox") return -1;
        return 0;
      }) || []
  };

  const totalGroupPages = Math.ceil(
    (currentGroup?.questions?.length || 0) / QUESTIONS_PER_PAGE
  );

  const startIndex = currentQuestionPage * QUESTIONS_PER_PAGE;
  const currentQuestions =
    (currentGroup?.questions
      ?.slice(startIndex, startIndex + QUESTIONS_PER_PAGE)
      ?.sort((a, b) => {
        if (a.ans_type === "dual_textbox" && b.ans_type !== "dual_textbox") return 1;
        if (b.ans_type === "dual_textbox" && a.ans_type !== "dual_textbox") return -1;
        return 0;
      })) || [];

  const isValidText = (val) => {
    if (!val) return false;
    const trimmed = String(val).trim();
    if (trimmed.length <= 1) return false;
    if (trimmed.split(/\s+/).length === 1) return false;
    return true;
  };

  const currentGroupName = currentGroup?.group || "";
  const allAnsweredCurrent = currentQuestions.every((q) => {
    const ans = answers[q.id];
    if (q.ans_type === "textbox") return isValidText(ans);
    if (q.ans_type === "dual_textbox") {
      const [field1, field2, disable1, disable2] = Array.isArray(ans)
        ? ans : ["", "", false, false];
      if (!disable1 && !isValidText(field1)) return false;
      if (!disable2 && !isValidText(field2)) return false;
      return true;
    }
    // Navigating Conflict: answer object { selected_answer, who_do_this }
    if (q.is_team_culture && currentGroupName === "Navigating Conflict") {
      return ans && ans.selected_answer;
    }
    // Johari Window: answer object { [letterA]: valueA, [letterB]: valueB }
    if (isType7) {
      return ans && typeof ans === "object" && !Array.isArray(ans) && Object.keys(ans).length === 2;
    }
    // All other team-culture multiselect + normal mcq/rating
    return ans && ans.length > 0;
  });

  const isLastGroup =
    currentGroupIndex === reorderedGroups.length - 1 &&
    currentQuestionPage === totalGroupPages - 1;

  const handleNext = () => {
    if (!allAnsweredCurrent) return;
    if (currentQuestionPage < totalGroupPages - 1) {
      setCurrentQuestionPage((p) => p + 1);
    } else {
      setCurrentGroupIndex((g) => g + 1);
      setCurrentQuestionPage(0);
    }
  };

  const handlePrev = () => {
    if (currentQuestionPage > 0) {
      setCurrentQuestionPage((p) => p - 1);
    } else {
      const prevGroup = currentGroupIndex - 1;
      setCurrentGroupIndex(prevGroup);
      const prevTotalPages = Math.ceil(
        (reorderedGroups[prevGroup]?.questions?.length || 0) / QUESTIONS_PER_PAGE
      );
      setCurrentQuestionPage(prevTotalPages - 1);
    }
  };

  const cleanDualTextboxAnswer = (ans) => {
    if (!Array.isArray(ans)) return ans;
    const [field1, field2, disable1, disable2] = ans;
    if (disable1 && disable2) return null;
    const cleaned = [];
    if (!disable1 && field1 && field1.trim() !== "") cleaned.push(field1.trim());
    if (!disable2 && field2 && field2.trim() !== "") cleaned.push(field2.trim());
    return cleaned.length > 0 ? cleaned : null;
  };

  // ✅ Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const allQuestions = reorderedGroups.flatMap((group) =>
      group.questions.map((q) => ({ ...q, _group_name: group.group }))
    );

    const payload = {
      ...state,
      survey_id: realSurveyId,
      company_id: companyFk,
      company_name: survey?.company_name,
      survey_name: survey?.survey_name,
      department_fk: isType3 || isType4 || isType5 || isType6 || isType7 || isType8 || isType9 || isType10 || isType11 || isType12 ? null : selectedDepartment,
      level_fk: isType3 || isType4 || isType5 || isType6 || isType7 || isType8 || isType9 || isType10 || isType11 || isType12? null : selectedLevel,
      name: isType3 || isType4 || isType5 || isType6 || isType7 || isType8 || isType9 || isType10 || isType11 || isType12? name : undefined,
      email: isType3 || isType4 || isType5 || isType6 || isType7 || isType8 || isType9 || isType10 || isType11 || isType12? email : undefined,
      phone: isType3 || isType4 || isType5 || isType6 || isType7 || isType8 || isType9 || isType10 || isType11 || isType12? phone : undefined,
      location: isType3 || isType4 || isType5 || isType6 || isType7 || isType8 || isType9 || isType10 || isType11 || isType12? location : undefined,
      custom_company: isType3 || isType4 || isType5 || isType6 || isType7 || isType8 || isType9 || isType10 || isType11 || isType12? companyText : undefined,
      custom_department: isType3 || isType4 || isType5 || isType6 || isType7 || isType8 || isType9 || isType10 || isType11 || isType12? departmentText : undefined,
      custom_level: isType3 || isType4 || isType5 || isType6 || isType7 || isType8 || isType9 || isType10 || isType11 || isType12? levelText : undefined,
      questions: allQuestions.map((q) => {
        // Navigating Conflict: answer is { selected_answer, who_do_this }
        if (q.is_team_culture && q._group_name === "Navigating Conflict") {
          const ans = answers[q.id] || {};
          return { ...q, selected_answer: ans.selected_answer || null, who_do_this: ans.who_do_this || false };
        }
        // ✅ Johari Window (isType7): send both the higher-scored letter as
        // selected_answer (kept consistent with every other survey type)
        // and the full 0–5 point split as option_scores, e.g. { A: 2, B: 3 }.
        // The /response endpoint should store option_scores in the new
        // `option_scores` JSONB column.
        if (isType7 && q.ans_type === "mcq") {
          const scores = answers[q.id] || {};
          const letters = Object.keys(scores);
          const topLetter = letters.reduce(
            (best, letter) => (scores[letter] > (scores[best] ?? -1) ? letter : best),
            letters[0]
          );
          return {
            ...q,
            selected_answer: topLetter ?? null,
            option_scores: letters.length ? scores : null,
          };
        }

        return {
          ...q,
          selected_answer: q.ans_type === "dual_textbox"
            ? cleanDualTextboxAnswer(answers[q.id]) : answers[q.id],
        };
      }),
    };

    try {
      await api.post("/response", payload);

      if (isType3) {
        try {
          const scoreRes = await api.post("/response/calculate-score", {
            survey_id: realSurveyId, email, phone
          });
          navigate("/5s-result", {
            state: { scores: scoreRes.data.data, name, email, phone }
          });
        } catch (calcError) {
          console.error("❌ Calculation API Error:", calcError);
          alert("Survey saved, but failed to calculate scores.");
          navigate("/thank-you");
        }
      } else if (isType4) {
        try {
          const scoreRes = await api.post("/response/calculate-score-vak", {
            survey_id: realSurveyId, email, phone
          });
          navigate("/vak-survey-result", {
            state: { scores: scoreRes.data.data, name, email, phone }
          });
        } catch (calcError) {
          console.error("❌ Calculation API Error:", calcError);
          alert("Survey saved, but failed to calculate scores.");
          navigate("/thank-you");
        }
      } else if (isType5) {
        try {
          const scoreRes = await api.post("/response/calculate-score/cpq", {
            survey_id: realSurveyId, email, phone
          });
          navigate("/cpq-result", {
            state: { scores: scoreRes.data.data, name, email, phone }
          });
        } catch (calcError) {
          console.error("❌ Calculation API Error:", calcError);
          alert("Survey saved, but failed to calculate scores.");
          navigate("/thank-you");
        }
      } else if (isType6) {
        try {
          const scoreRes = await api.post("/response/calculate-score/ems", {
            survey_id: realSurveyId, email, phone
          });
          navigate("/ems-result", {
            state: { scores: scoreRes.data.data, name, email, phone }
          });
        } catch (calcError) {
          console.error("❌ EMS Calculation API Error:", calcError);
          alert("Survey saved, but failed to calculate scores.");
          navigate("/thank-you");
        }
      } else if (isType7) {
        try {
          const scoreRes = await api.post("/response/calculate-score/jh", {
            survey_id: realSurveyId, email, phone
          });
          navigate("/jh-result", {
            state: { scores: scoreRes.data.data, name, email, phone }
          });
        } catch (calcError) {
          console.error("❌ JH Calculation API Error:", calcError);
          alert("Survey saved, but failed to calculate scores.");
          navigate("/thank-you");
        }
      } else if (isType8) {
        try {
          const scoreRes = await api.post("/response/calculate-score/tk", {
            survey_id: realSurveyId, email, phone
          });
          navigate("/tk-result", {
            state: { scores: scoreRes.data.data, name, email, phone }
          });
        } catch (calcError) {
          console.error("❌ TK Calculation API Error:", calcError);
          alert("Survey saved, but failed to calculate scores.");
          navigate("/thank-you");
        }
      } else if (isType9) {
        try {
          const scoreRes = await api.post("/response/calculate-score/ao", {
            survey_id: realSurveyId, email, phone
          });
          navigate("/ao-result", {
            state: { scores: scoreRes.data.data, name, email, phone, }
          });
        } catch (calcError) {
          console.error("❌ AO Calculation API Error:", calcError);
          alert("Survey saved, but failed to calculate scores.");
          navigate("/thank-you");
        }
      } else if (isType10) {
        try {
          const scoreRes = await api.post("/response/calculate-score/negotiation", {
            survey_id: realSurveyId, email, phone
          });
          navigate("/ns-result", {
            state: { scores: scoreRes.data, name, email, phone }
          });
        } catch (calcError) {
          console.error("❌ Negotiation Calculation API Error:", calcError);
          alert("Survey saved, but failed to calculate scores.");
          navigate("/thank-you");
        }
      } else if (isType11) {
        try {
          const scoreRes = await api.post("/response/calculate-score/loco", {
            survey_id: realSurveyId, email, phone
          });
          navigate("/loco-results", {
            state: { scores: scoreRes.data, name, email, phone }
          });
        } catch (calcError) {
          console.error("❌ LOCO Calculation API Error:", calcError);
          alert("Survey saved, but failed to calculate scores.");
          navigate("/thank-you");
        }
      }
         else if (isType12) {
        try {
          const scoreRes = await api.post("/response/calculate-score/t90", {
            survey_id: realSurveyId, email, phone
          });
          navigate("/thank-you");
          // navigate("/t90-results", {
          //   state: { scores: scoreRes.data, name, email, phone }
          // });
        } catch (calcError) {
          console.error("❌ LOCO Calculation API Error:", calcError);
          alert("Survey saved, but failed to calculate scores.");
          navigate("/thank-you");
        }
      } else {
        navigate("/thank-you");
      }
    } catch (err) {
      console.error("❌ Submit failed entirely:", err);
      alert("Failed to save survey. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loader-container"><div className="loader"></div><p>Loading Survey...</p></div>;
  if (submitting) return <div className="loader-container"><div className="loader"></div><p>Submitting...</p></div>;


  // INSTRUCTIONS 
  if (!showSurvey && isType3 && !instructionsRead) {
    return (
      <div className="survey-wrapper">
        <div className="survey-card" style={{ textAlign: "left", maxWidth: "800px", margin: "0 auto", color: "#000", fontFamily: "Arial, sans-serif" }}>

          <h2 style={{ textAlign: "center", fontSize: "26px", fontWeight: "bold", marginBottom: "20px", color: "#000" }}>
            Welcome
          </h2>

          <div style={{ textAlign: "center", marginBottom: "25px" }}>
            <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "5px" }}>
              5S Leadership Communication Style Survey
            </div>
            <div style={{ fontStyle: "italic", fontSize: "15px" }}>
              Communication Effectiveness & Adaptability Descriptor (CEAD)
            </div>
          </div>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "30px", textAlign: "left" }}>
            This survey will help you discover your dominant leadership communication style — the way you naturally direct, guide, and empower the people around you. It takes approximately <strong>8–10 minutes</strong> to complete.
          </p>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📋 What to Expect
          </h3>
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>You will be presented with <strong>12 real-world leadership situations</strong></li>
            <li>Each situation is followed by <strong>4 possible actions (A, B, C, D)</strong></li>
            <li>At the end, you will receive a <strong>personalised style profile</strong> mapped to the 5S Leadership Ladder</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ✅ How to Take the Survey
          </h3>
          <ol start="1" style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px" }}>
            <li><strong>Read each situation carefully.</strong> Picture yourself in a leadership or managerial role as you read.</li>
            <li><strong>Choose only one response</strong> — the option that best describes how <em>you have actually behaved</em> in the past, not what you think the ideal leader should do.</li>
            <li><strong>Do not overthink.</strong> Go with your first instinct — your gut response reflects your natural style most accurately.</li>
            <li><strong>Answer in order.</strong> Complete Question 1 before moving to Question 2, and so on. Do not skip ahead or go back.</li>
            <li><strong>There are no right or wrong answers.</strong> Every style has strengths. Answer honestly for the most accurate result.</li>
          </ol>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ⚡ A Few Important Reminders
          </h3>
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>Respond as a <strong>leader or manager</strong>, not as a peer or subordinate</li>
            <li>Base your answers on <strong>your most frequent leadership context</strong> — the team or environment you lead most often</li>
            <li>Do <strong>not</strong> go back and change responses once submitted</li>
            <li>Each question must be answered before you can proceed to the next</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "30px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📊 Understanding Your Results
          </h3>

          <div style={{ borderTop: "1px solid #3498db", borderBottom: "1px solid #3498db", padding: "15px 0", margin: "20px 0", textAlign: "center" }}>
            <p style={{ fontStyle: "italic", color: "#3498db", margin: 0, fontWeight: "500", fontSize: "15px" }}>
              Your dominant style tells you how you lead, your Adaptability Score tells you how well you read the situation before leading.
            </p>
          </div>

          <p style={{ marginBottom: "15px", fontSize: "15px" }}>Once you complete all 12 situations, your responses will be scored across the <strong>5S Leadership Communication Styles</strong>:</p>

          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "40px", fontSize: "14px", border: "2px solid #000" }}>
            <thead>
              <tr>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left", width: "25%" }}>Style</th>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left" }}>What It Means</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Signalling</td><td style={{ padding: "12px", border: "1px solid #000" }}>You direct clearly — giving explicit instructions and structure</td></tr>
              <tr><td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Stimulating</td><td style={{ padding: "12px", border: "1px solid #000" }}>You motivate — encouraging initiative while still providing direction</td></tr>
              <tr><td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Suggesting</td><td style={{ padding: "12px", border: "1px solid #000" }}>You guide — co-creating solutions and inviting input</td></tr>
              <tr><td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Sharing</td><td style={{ padding: "12px", border: "1px solid #000" }}>You collaborate — solving problems together as equals</td></tr>
              <tr><td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Sanctioning</td><td style={{ padding: "12px", border: "1px solid #000" }}>You empower — fully delegating ownership and decision-making</td></tr>
            </tbody>
          </table>

          <div style={{ textAlign: "center", marginBottom: "30px" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              💡 Before You Begin
            </h3>
            <p style={{ fontStyle: "italic", color: "#000", marginBottom: "20px", fontSize: "15px", fontWeight: "500" }}>
              "The best leaders don't stay at the top or bottom of the ladder. They move up and down depending on what the person needs."
            </p>
            <p style={{ color: "#000", fontSize: "15px" }}>When you're ready, click "<strong>Start Survey</strong>" below.</p>
          </div>

          <div style={{ textAlign: "center", paddingBottom: "20px" }}>
            <button className="nav-btn" onClick={() => setInstructionsRead(true)} style={{ fontSize: "16px", padding: "12px 40px", fontWeight: "bold" }}>
              Start Survey
            </button>
          </div>
        </div>
      </div>
    );
  }

  // INSTRUCTIONS for isType4
  if (!showSurvey && isType4 && !instructionsRead) {
    return (
      <div className="survey-wrapper">
        <div className="survey-card" style={{ textAlign: "left", maxWidth: "800px", margin: "0 auto", color: "#000", fontFamily: "Arial, sans-serif" }}>

          <h2 style={{ textAlign: "center", fontSize: "26px", fontWeight: "bold", marginBottom: "20px", color: "#000" }}>
            Welcome
          </h2>

          <div style={{ textAlign: "center", marginBottom: "25px" }}>
            <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "5px" }}>
              Visual-Auditory-Kinesthetic Survey
            </div>
            <div style={{ fontStyle: "italic", fontSize: "15px" }}>
              Understanding Your Mental Map
            </div>
          </div>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "30px", textAlign: "left" }}>
            This survey will help you discover your dominant leadership communication style — the way you naturally direct, guide, and empower the people around you. It takes approximately <strong>8–10 minutes</strong> to complete.
          </p>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📋 What to Expect
          </h3>
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>You will be presented with <strong>20 real-world leadership situations</strong></li>
            <li>Each situation is followed by <strong>3 possible actions (A, B, C)</strong></li>
            <li>At the end, you will receive a <strong>personalised learning style profile</strong> based on the VAK model (Visual, Auditory, Kinesthetic).</li>
          </ul>



          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ✅ How to Take the Survey
          </h3>

          {/* <--Choose only one response — the option that best reflects your natural preference, not what you think is the ideal or correct choice. */}


          <ol start="1" style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px" }}>
            <li><strong>Read each situation carefully.</strong> Imagine how you would naturally respond or process the information.</li>
            <li><strong>Choose only one response</strong> — the option that best reflects <em>your natural preference,</em> not what you think is the ideal or correct choice.</li>
            <li><strong>Do not overthink.</strong> Go with your first instinct — your gut response reflects your natural style most accurately.</li>
            <li><strong>Answer in order.</strong> Complete Question 1 before moving to Question 2, and so on. Do not skip ahead or go back.</li>
            <li><strong>There are no right or wrong answers.</strong> Every style has strengths. Answer honestly for the most accurate result.</li>
          </ol>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ⚡ A Few Important Reminders
          </h3>

          {/* Respond based on your natural learning and communication preferences, rather than any specific role. */}
          {/* Base your answers on how you typically understand and process information in everyday situations. */}
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>Respond based on <strong>your natural learning and communication preferences, </strong>rather than any specific role.</li>
            <li>Base your answers on <strong>how you typically understand and process information</strong> in everyday situations.</li>
            <li>Do <strong>not</strong> go back and change responses once submitted</li>
            <li>Each question must be answered before you can proceed to the next</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "30px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📊 Understanding Your Results
          </h3>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            Once you complete the survey, your responses will reveal your <strong>dominant learning style</strong> based on the VAK model.
          </p>

          <div style={{ borderTop: "1px solid #3498db", borderBottom: "1px solid #3498db", padding: "15px 0", margin: "20px 0", textAlign: "center" }}>
            <p style={{ fontStyle: "italic", color: "#3498db", margin: 0, fontWeight: "500", fontSize: "15px" }}>
              Your dominant style shows how you naturally process information and communicate with others.
            </p>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "30px", fontSize: "14px", border: "2px solid #000" }}>
            <thead>
              <tr>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left", width: "25%" }}>Style</th>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left" }}>What It Means</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Visual</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  You understand best through images, diagrams, and mental pictures. You tend to think in visuals and remember what you see.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Auditory</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  You learn best through listening. Tone, pitch, and rhythm matter to you, and you process information through sound and conversation.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Kinesthetic</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  You learn best through experience, movement, and feelings. You prefer hands-on activities and rely on intuition and physical interaction.
                </td>
              </tr>
            </tbody>
          </table>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            While you may have one dominant style, you can adapt and use all three depending on the situation. Understanding your style helps you improve learning, communication, and connection with others.
          </p>
          <p style={{ color: "#000", fontSize: "15px" }}>When you're ready, click "<strong>Start Survey</strong>" below.</p>
          <div style={{ textAlign: "center", paddingBottom: "20px" }}>
            <button className="nav-btn" onClick={() => setInstructionsRead(true)} style={{ fontSize: "16px", padding: "12px 40px", fontWeight: "bold" }}>
              Start Survey
            </button>
          </div>
        </div>
      </div>
    );
  }
  // Instructions for isType5
  if (!showSurvey && isType5 && !instructionsRead) {
    return (
      <div className="survey-wrapper">
        <div className="survey-card" style={{ textAlign: "left", maxWidth: "800px", margin: "0 auto", color: "#000", fontFamily: "Arial, sans-serif" }}>

          <h2 style={{ textAlign: "center", fontSize: "26px", fontWeight: "bold", marginBottom: "20px", color: "#000" }}>
            Welcome
          </h2>

          <div style={{ textAlign: "center", marginBottom: "25px" }}>
            <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "5px" }}>
              Communication Proficiency Survey
            </div>
            <div style={{ fontStyle: "italic", fontSize: "15px" }}>
              Understanding Your Communication Profile
            </div>
          </div>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "30px", textAlign: "left" }}>
            This survey will help you assess your communication proficiency — how effectively you express, listen, and interact with others. It takes approximately <strong>8–10 minutes</strong> to complete.
          </p>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📋 What to Expect
          </h3>
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>You will be presented with <strong>48 statements about your communication behaviour</strong> in real-world situations.</li>
            <li>Each situation is followed by <strong>5 possible actions (Always, Usually, Sometimes, Rarely, Never)</strong></li>
            <li>At the end, you will receive a <strong>communication proficiency report</strong> outlining your strengths and development areas.</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ✅ How to Take the Survey
          </h3>

          {/* <--Choose only one response — the option that best reflects your natural preference, not what you think is the ideal or correct choice. */}


          <ol start="1" style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px" }}>
            Respond based on your natural communication style, rather than any specific role.
            <li><strong>Read each situation carefully.</strong> Imagine how you would naturally respond or process the information.</li>
            <li><strong>Choose only one response</strong> — the option that best reflects <em>your natural preference,</em> not what you think is the ideal or correct choice.</li>
            <li><strong>Do not overthink.</strong> Go with your first instinct — your gut response reflects your natural style most accurately.</li>
            <li><strong>Answer in order.</strong> Complete Question 1 before moving to Question 2, and so on. Do not skip ahead or go back.</li>
            <li><strong>There are no right or wrong answers.</strong> Every style has strengths. Answer honestly for the most accurate result.</li>
          </ol>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ⚡ A Few Important Reminders
          </h3>

          {/* Respond based on your natural learning and communication preferences, rather than any specific role. */}
          {/* Base your answers on how you typically understand and process information in everyday situations. */}
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>Respond based on <strong>your natural learning and communication preferences, </strong>rather than any specific role.</li>
            <li>Base your answers on <strong>how you typically understand and process information</strong> in everyday situations.</li>
            <li>Do <strong>not</strong> go back and change responses once submitted</li>
            <li>Each question must be answered before you can proceed to the next</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "30px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📊 Understanding Your Results
          </h3>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            Once you complete the survey, your responses will <strong>provide a clear profile of your communication strengths and development areas.
            </strong> based on the CPQ model.
          </p>


          <div style={{ borderTop: "1px solid #3498db", borderBottom: "1px solid #3498db", padding: "15px 0", margin: "20px 0", textAlign: "center" }}>
            <p style={{ fontStyle: "italic", color: "#3498db", margin: 0, fontWeight: "500", fontSize: "15px" }}>
              Your dominant style shows how you naturally process information and communicate with others.
            </p>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "30px", fontSize: "14px", border: "2px solid #000" }}>
            <thead>
              <tr>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left", width: "25%" }}>Style</th>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left" }}>What It Means</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Information Sharing</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Ability to share information clearly, accurately, and in a timely manner.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Talks with Purpose</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Communicates with clear intent, ensuring conversations are focused and goal-oriented.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Listening</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Effectively understands and processes information by actively listening to others.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Non-Verbal Cues</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Uses and interprets body language, facial expressions, and tone to enhance communication.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Clarity & Brevity</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Expresses ideas in a clear, concise, and easy-to-understand manner.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Articulation</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Uses appropriate language, structure, and fluency to convey messages effectively.
                </td>
              </tr>
            </tbody>
          </table>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            While you may have stronger communication areas, you can develop and adapt all dimensions based on the situation. Understanding your profile helps you communicate more effectively, improve clarity, and build stronger connections with others.
          </p>
          <p style={{ color: "#000", fontSize: "15px" }}>When you're ready, click "<strong>Start Survey</strong>" below.</p>
          <div style={{ textAlign: "center", paddingBottom: "20px" }}>
            <button className="nav-btn" onClick={() => setInstructionsRead(true)} style={{ fontSize: "16px", padding: "12px 40px", fontWeight: "bold" }}>
              Start Survey
            </button>
          </div>
        </div>
      </div>
    );
  }

  //EMS type 6
  if (!showSurvey && isType6 && !instructionsRead) {
    return (
      <div className="survey-wrapper">
        <div className="survey-card" style={{ textAlign: "left", maxWidth: "800px", margin: "0 auto", color: "#000", fontFamily: "Arial, sans-serif" }}>

          <h2 style={{ textAlign: "center", fontSize: "26px", fontWeight: "bold", marginBottom: "20px", color: "#000" }}>
            Welcome
          </h2>

          <div style={{ textAlign: "center", marginBottom: "25px" }}>
            <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "5px" }}>
              Leadership Styles Survey
            </div>
            <div style={{ fontStyle: "italic", fontSize: "15px" }}>
              Understanding Your Leadershipo Style
            </div>
          </div>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "30px", textAlign: "left" }}>
            This survey helps you identify your dominant leadership styles and understand how you naturally direct, guide, and influence others. It takes approximately<strong> 8–10 minutes</strong> to complete.
          </p>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📋 What to Expect
          </h3>
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>You will be presented with <strong>36 real-world leadership situations</strong></li>
            <li>Each item presents two statements <strong>(A and B).</strong> Choose the one that best reflects how you typically behave.</li>
            <li>At the end, you will receive a <strong>detailed leadership report</strong> dominant leadership styles, motivational drivers, and key behavioural insights.
            </li>
          </ul>
          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ✅ How to Take the Survey
          </h3>

          {/* <--Choose only one response — the option that best reflects your natural preference, not what you think is the ideal or correct choice. */}


          <ol start="1" style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px" }}>
            Respond based on your natural leadership tendencies, rather than any specific role or expectation.
            <li><strong>Read each situation carefully.</strong> Imagine how you would naturally respond or process the information.</li>
            <li><strong>Choose only one response</strong> — the option that best reflects <em>your natural preference,</em> not what you think is the ideal or correct choice.</li>
            <li><strong>Do not overthink.</strong> Go with your first instinct — your gut response reflects your natural style most accurately.</li>
            <li><strong>Answer in order.</strong> Complete Question 1 before moving to Question 2, and so on. Do not skip ahead or go back.</li>
            <li><strong>There are no right or wrong answers.</strong> Every style has strengths. Answer honestly for the most accurate result.</li>
          </ol>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ⚡ A Few Important Reminders
          </h3>

          {/* Respond based on your natural learning and communication preferences, rather than any specific role. */}
          {/* Base your answers on how you typically understand and process information in everyday situations. */}
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>Respond based on <strong>your leadership tendencies, </strong>rather than any specific role.</li>
            <li>Base your answers on <strong>how you typically lead, make decisions, and interact with others</strong> in real situations.</li>
            <li>Do <strong>not</strong> go back and change responses once submitted</li>
            <li>Each question must be answered before you can proceed to the next</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "30px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📊 Understanding Your Results
          </h3>
          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            Once you complete the survey, your responses will <strong>provide a clear profile of your dominant leadership styles, core motivational drivers, and key behavioural insights
            </strong> based on the Leadership Styles model.
          </p>


          <div style={{ borderTop: "1px solid #3498db", borderBottom: "1px solid #3498db", padding: "15px 0", margin: "20px 0", textAlign: "center" }}>
            <p style={{ fontStyle: "italic", color: "#3498db", margin: 0, fontWeight: "500", fontSize: "15px" }}>
              Your dominant style reflects how you naturally lead, make decisions, and influence others.
            </p>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "30px", fontSize: "14px", border: "2px solid #000" }}>
            <thead>
              <tr>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left", width: "25%" }}>Style</th>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left" }}>What It Means</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Coercer</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Drives immediate compliance through authority and strict control.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Pacesetter</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Sets high performance standards and leads by example to drive results.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Authoritarian</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Provides clear direction and vision, guiding others toward defined goals.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Affiliator</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Builds strong relationships and prioritizes harmony within the team.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Democrat</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Encourages participation and values team input in decision-making.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Coach</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Focuses on developing individuals for long-term growth and improvement.
                </td>
              </tr>
            </tbody>
          </table>


          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            While you may have stronger leadership styles, effective leaders adapt their approach based on the situation. Understanding your profile helps you lead more effectively, make better decisions, and influence others with greater impact.
          </p>
          <p style={{ color: "#000", fontSize: "15px" }}>When you're ready, click "<strong>Start Survey</strong>" below.</p>
          <div style={{ textAlign: "center", paddingBottom: "20px" }}>
            <button className="nav-btn" onClick={() => setInstructionsRead(true)} style={{ fontSize: "16px", padding: "12px 40px", fontWeight: "bold" }}>
              Start Survey
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!showSurvey && isType7 && !instructionsRead) {
    return (
      <div className="survey-wrapper">
        <div className="survey-card" style={{ textAlign: "left", maxWidth: "800px", margin: "0 auto", color: "#000", fontFamily: "Arial, sans-serif" }}>

          <h2 style={{ textAlign: "center", fontSize: "26px", fontWeight: "bold", marginBottom: "20px", color: "#000" }}>
            Welcome
          </h2>

          <div style={{ textAlign: "center", marginBottom: "25px" }}>
            <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "5px" }}>
              Johari Window Self-Awareness Survey
            </div>
            <div style={{ fontStyle: "italic", fontSize: "15px" }}>
              Understanding How You See Yourself and How Others Perceive You
            </div>
          </div>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "30px", textAlign: "left" }}>
            This survey is designed to help you explore your self-awareness, interpersonal behaviour, and how your personality is perceived by others. It will give you insights into your Open, Blind, Hidden, and Unknown areas based on the Johari Window model. It takes approximately<strong> 8–10 minutes</strong> to complete.
          </p>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📋 What to Expect
          </h3>
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>You will be presented with <strong>20 personality-related questions</strong></li>
            <li>Each question will have a set of 2 options — choose the one that best describes you</li>
            <li>At the end, you will receive a report highlighting your <strong>Johari Window</strong> profile
            </li>
          </ul>
          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ✅ How to Take the Survey
          </h3>

          {/* <--Choose only one response — the option that best reflects your natural preference, not what you think is the ideal or correct choice. */}


          <ol start="1" style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px" }}>
            Respond based on your true personality - not what you think is ideal
            <li><strong>Read each situation carefully.</strong> Imagine how you would naturally respond or process the information.</li>
            <li><strong>Choose only one response</strong> — the option that best reflects <em>your natural preference,</em> not what you think is the ideal or correct choice.</li>
            <li><strong>Do not overthink.</strong> Go with your first instinct — your gut response reflects your natural style most accurately.</li>
            <li><strong>Answer in order.</strong> Complete Question 1 before moving to Question 2, and so on. Do not skip ahead or go back.</li>
            <li><strong>There are no right or wrong answers.</strong> Every style has strengths. Answer honestly for the most accurate result.</li>
          </ol>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ⚡ A Few Important Reminders
          </h3>

          {/* Respond based on your natural learning and communication preferences, rather than any specific role. */}
          {/* Base your answers on how you typically understand and process information in everyday situations. */}
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>Answer based on your <strong>real-life behaviour</strong> and experiences  </li>
            <li>Base your answers on <strong>your true personality</strong> rather than thinking what is the ideal personality.</li>
            <li>Do <strong>not</strong> go back and change responses once submitted</li>
            <li>Each question must be answered before you can proceed to the next</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "30px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📊 Understanding Your Results
          </h3>
          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            Once you complete the survey, your responses will provide a clear profile of your <strong>Johari Window</strong>, highlighting your<strong> Arena, Blind, Conceal, and Discover areas</strong> along with key insights into your self-awareness and interpersonal behaviour.
          </p>


          <div style={{ borderTop: "1px solid #3498db", borderBottom: "1px solid #3498db", padding: "15px 0", margin: "20px 0", textAlign: "center" }}>
            <p style={{ fontStyle: "italic", color: "#3498db", margin: 0, fontWeight: "500", fontSize: "15px" }}>
              Your Johari Window reflects how you understand yourself and how others perceive you in everyday interactions.
            </p>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "30px", fontSize: "14px", border: "2px solid #000" }}>
            <thead>
              <tr>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left", width: "25%" }}>Area</th>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left" }}>What It Means</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Arena</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Traits and behaviours known to both you and others, supporting clear communication and trust.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Blind</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Aspects others can see in you, but you may not be aware of yourself.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Conceal</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Personal thoughts, feelings, and traits that you choose not to reveal to others.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Discover</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Untapped potential, abilities, or behaviours that are not yet discovered by you or others.
                </td>
              </tr>

            </tbody>
          </table>


          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            While you may have stronger areas within your Johari Window, developing self-awareness involves continuously expanding your Open Area by sharing, reflecting, and seeking feedback. Understanding your profile helps you improve communication, build stronger relationships, and grow personally.
          </p>
          <p style={{ color: "#000", fontSize: "15px" }}>When you're ready, click "<strong>Start Survey</strong>" below.</p>
          <div style={{ textAlign: "center", paddingBottom: "20px" }}>
            <button className="nav-btn" onClick={() => setInstructionsRead(true)} style={{ fontSize: "16px", padding: "12px 40px", fontWeight: "bold" }}>
              Start Survey
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!showSurvey && isType8 && !instructionsRead) {
    return (
      <div className="survey-wrapper">
        <div className="survey-card" style={{ textAlign: "left", maxWidth: "800px", margin: "0 auto", color: "#000", fontFamily: "Arial, sans-serif" }}>

          <h2 style={{ textAlign: "center", fontSize: "26px", fontWeight: "bold", marginBottom: "20px", color: "#000" }}>
            Welcome
          </h2>

          <div style={{ textAlign: "center", marginBottom: "25px" }}>
            <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "5px" }}>
              Thomas-Kilmann Conflict Mode Instrument
            </div>
            <div style={{ fontStyle: "italic", fontSize: "15px" }}>
              Understanding How You Handle Conflict and Disagreement
            </div>
          </div>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "30px", textAlign: "left" }}>
            This survey is designed to help you explore your conflict-handling behaviour and understand how you respond in situations where two people's concerns appear to be incompatible. It will give you insights into your preferred conflict modes — Competing, Collaborating, Compromising, Avoiding, and Accommodating. It takes approximately <strong>8–10 minutes</strong> to complete.
          </p>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📋 What to Expect
          </h3>
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>You will be presented with <strong>30 pairs of statements</strong> related to conflict situations</li>
            <li>Each pair has 2 options (A or B) — choose the one that best describes how you typically behave</li>
            <li>At the end, you will receive a report highlighting your <strong>Thomas-Kilmann Conflict Mode</strong> profile</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ✅ How to Take the Survey
          </h3>

          <ol start="1" style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px" }}>
            Respond based on your true behaviour — not what you think is ideal
            <li><strong>Read each pair of statements carefully.</strong> Imagine how you would naturally respond in a real conflict or disagreement.</li>
            <li><strong>Choose only one response</strong> — the option that best reflects <em>your natural preference,</em> not what you think is the correct or professional choice.</li>
            <li><strong>Do not overthink.</strong> Go with your first instinct — your gut response reflects your natural style most accurately.</li>
            <li><strong>Answer in order.</strong> Complete Question 1 before moving to Question 2, and so on. Do not skip ahead or go back.</li>
            <li><strong>There are no right or wrong answers.</strong> All five conflict modes have their strengths. Answer honestly for the most accurate result.</li>
          </ol>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ⚡ A Few Important Reminders
          </h3>

          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>Answer based on your <strong>real-life behaviour</strong> in conflict or disagreement situations</li>
            <li>Base your answers on <strong>your true reactions</strong> rather than thinking about what the ideal response would be</li>
            <li>Do <strong>not</strong> go back and change responses once submitted</li>
            <li>Each question must be answered before you can proceed to the next</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "30px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📊 Understanding Your Results
          </h3>
          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            Once you complete the survey, your responses will generate a clear profile of your <strong>Conflict Mode preferences</strong>, highlighting your dominant and secondary styles along with key insights into your assertiveness and cooperativeness in conflict situations.
          </p>

          <div style={{ borderTop: "1px solid #3498db", borderBottom: "1px solid #3498db", padding: "15px 0", margin: "20px 0", textAlign: "center" }}>
            <p style={{ fontStyle: "italic", color: "#3498db", margin: 0, fontWeight: "500", fontSize: "15px" }}>
              Your conflict mode profile reflects how you balance your own concerns with those of others in everyday disagreements and disputes.
            </p>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "30px", fontSize: "14px", border: "2px solid #000" }}>
            <thead>
              <tr>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left", width: "25%" }}>Mode</th>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left" }}>What It Means</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Competing</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Assertive and uncooperative — pursuing your own concerns firmly, even at the expense of others. Often used when quick, decisive action is needed.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Collaborating</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Assertive and cooperative — working with the other person to find a solution that fully satisfies both parties' concerns.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Compromising</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Intermediate in both assertiveness and cooperation — finding an expedient, mutually acceptable solution that partially satisfies both parties.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Avoiding</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Unassertive and uncooperative — sidestepping or postponing the conflict without addressing either party's concerns directly.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Accommodating</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Unassertive and cooperative — neglecting your own concerns to satisfy the other person's needs, often as a goodwill gesture.
                </td>
              </tr>
            </tbody>
          </table>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            While you may naturally favour certain conflict modes, each of the five styles has its place depending on the situation. Understanding your profile helps you recognise when to assert, when to collaborate, and when to step back — enabling you to handle conflict more effectively and build stronger professional relationships.
          </p>
          <p style={{ color: "#000", fontSize: "15px" }}>When you're ready, click "<strong>Start Survey</strong>" below.</p>
          <div style={{ textAlign: "center", paddingBottom: "20px" }}>
            <button className="nav-btn" onClick={() => setInstructionsRead(true)} style={{ fontSize: "16px", padding: "12px 40px", fontWeight: "bold" }}>
              Start Survey
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!showSurvey && isType9 && !instructionsRead) {
    return (
      <div className="survey-wrapper">
        <div className="survey-card" style={{ textAlign: "left", maxWidth: "800px", margin: "0 auto", color: "#000", fontFamily: "Arial, sans-serif" }}>

          <h2 style={{ textAlign: "center", fontSize: "26px", fontWeight: "bold", marginBottom: "20px", color: "#000" }}>
            Welcome
          </h2>

          <div style={{ textAlign: "center", marginBottom: "25px" }}>
            <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "5px" }}>
              Achievement Orientation Questionnaire
            </div>
            <div style={{ fontStyle: "italic", fontSize: "15px" }}>
              Understanding Your Drive, Motivation, and Orientation Towards Work
            </div>
          </div>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "30px", textAlign: "left" }}>
            This standardised survey, designed by <strong>Prayag Mehta</strong>, is designed to help you self-evaluate your Achievement Orientation. It will give you insights into whether you are driven by personal goals and challenges, reliable task completion, or external factors — based on three key orientations: <strong>Achievement Related (AR), Task Related (TR),</strong> and <strong>Unrelated to Achievement (UR)</strong>. It takes approximately <strong>8–10 minutes</strong> to complete.
          </p>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📋 What to Expect
          </h3>
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>You will be presented with <strong>22 everyday situations</strong> involving people engaged in various activities</li>
            <li>Each situation will have <strong>6 possible interpretations (a–f)</strong> — select the one that feels most appropriate or closest to how you would see it</li>
            <li>At the end, you will receive a report highlighting your <strong>Achievement Orientation</strong> profile — AR, TR, UR, or a combination</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ✅ How to Take the Survey
          </h3>

          <ol start="1" style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px" }}>
            Respond based on your true instinct — not what you think is ideal
            <li><strong>Read each situation carefully.</strong> Picture the scenario and consider what feels like the most natural interpretation to you.</li>
            <li><strong>Choose only one response</strong> — the option that best reflects <em>your natural instinct,</em> not what you think is the most impressive or correct answer.</li>
            <li><strong>Do not overthink.</strong> Go with your first instinct — your gut response is the most accurate reflection of your orientation.</li>
            <li><strong>Answer in order.</strong> Complete Question 1 before moving to Question 2, and so on. Do not skip ahead or go back.</li>
            <li><strong>There are no right or wrong answers.</strong> Each orientation profile has its own strengths. Answer honestly for the most meaningful result.</li>
          </ol>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ⚡ A Few Important Reminders
          </h3>

          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>Answer based on your <strong>real-life attitude</strong> towards work, goals, and challenges</li>
            <li>Base your answers on <strong>your true motivations</strong> rather than what you think an ideal professional should say</li>
            <li>Do <strong>not</strong> go back and change responses once submitted</li>
            <li>Each question must be answered before you can proceed to the next</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "30px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📊 Understanding Your Results
          </h3>
          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            Once you complete the survey, your responses will generate a clear profile of your <strong>Achievement Orientation</strong>, identifying your dominant category — AR, TR, UR, or a combination — along with personalised insights into your motivations and work style.
          </p>

          <div style={{ borderTop: "1px solid #3498db", borderBottom: "1px solid #3498db", padding: "15px 0", margin: "20px 0", textAlign: "center" }}>
            <p style={{ fontStyle: "italic", color: "#3498db", margin: 0, fontWeight: "500", fontSize: "15px" }}>
              Your Achievement Orientation profile reflects how you relate to goals, challenges, and work — and what truly drives you.
            </p>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "30px", fontSize: "14px", border: "2px solid #000" }}>
            <thead>
              <tr>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left", width: "25%" }}>Orientation</th>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left" }}>What It Means</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Achievement Related (AR)</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  You set your own goals, seek challenges, and are driven by personal growth. You thrive with autonomy and are a key asset for any organisation's progress.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Task Related (TR)</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  You are highly reliable and committed to completing assigned work within timelines. You work well under supervision and respond positively to clearly defined targets.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>High AR & TR</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  You combine reliability with ambition. You adapt to the demands of your role — dependable at early stages, and increasingly self-driven as you grow in your career.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Unrelated (UR)</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Your motivation is primarily external, such as earnings or routine. Challenges and self-set goals are less of a priority — though this is an area with significant growth potential.
                </td>
              </tr>
            </tbody>
          </table>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            While you may lean towards a particular orientation, understanding your profile helps you become more conscious of your motivations, make better career choices, and take deliberate steps towards greater achievement and personal growth.
          </p>
          <p style={{ color: "#000", fontSize: "15px" }}>When you're ready, click "<strong>Start Survey</strong>" below.</p>
          <div style={{ textAlign: "center", paddingBottom: "20px" }}>
            <button className="nav-btn" onClick={() => setInstructionsRead(true)} style={{ fontSize: "16px", padding: "12px 40px", fontWeight: "bold" }}>
              Start Survey
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!showSurvey && isType10 && !instructionsRead) {
    return (
      <div className="survey-wrapper">
        <div className="survey-card" style={{ textAlign: "left", maxWidth: "800px", margin: "0 auto", color: "#000", fontFamily: "Arial, sans-serif" }}>

          <h2 style={{ textAlign: "center", fontSize: "26px", fontWeight: "bold", marginBottom: "20px", color: "#000" }}>
            Welcome
          </h2>

          <div style={{ textAlign: "center", marginBottom: "25px" }}>
            <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "5px" }}>
              Negotiating Style Survey
            </div>
            <div style={{ fontStyle: "italic", fontSize: "15px" }}>
              Understanding Your Natural Approach to Negotiation and Conflict
            </div>
          </div>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "30px", textAlign: "left" }}>
            This survey, based on the framework by <strong>Holley, Jennings & Wolters</strong> (8th ed., Chapter 6), is designed to help you identify your natural negotiating style. It measures how you balance <strong>assertiveness</strong> and <strong>cooperativeness</strong> across five recognised styles: <strong>Competing (CO), Collaborating (CL), Compromising (CM), Accommodating (AC),</strong> and <strong>Avoiding (AV)</strong>. It takes approximately <strong>5–7 minutes</strong> to complete.
          </p>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📋 What to Expect
          </h3>
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>You will be presented with a series of <strong>statements about how you behave in negotiation and conflict situations</strong></li>
            <li>For each statement, indicate how closely it reflects your typical approach — there are <strong>no trick questions</strong></li>
            <li>At the end, you will receive a profile showing your scores across <strong>all five negotiating styles</strong> and your dominant style(s)</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ✅ How to Take the Survey
          </h3>
          <ol style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px" }}>
            <li><strong>Read each statement carefully.</strong> Think about how you actually behave in real negotiation or conflict situations — not how you aspire to behave.</li>
            <li><strong>Respond honestly.</strong> Choose the option that most accurately reflects your natural instinct, not the answer that sounds most professional.</li>
            <li><strong>Do not overthink.</strong> Your first response is usually the most accurate reflection of your genuine style.</li>
            <li><strong>Answer in order.</strong> Complete each question before moving to the next. Do not skip ahead or go back.</li>
            <li><strong>There are no right or wrong answers.</strong> Every negotiating style has strengths and appropriate contexts. Honest answers produce the most useful result.</li>
          </ol>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ⚡ A Few Important Reminders
          </h3>
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>Answer based on your <strong>real behaviour</strong> in negotiations — not your ideal or intended behaviour</li>
            <li>Think about <strong>professional situations</strong> such as salary discussions, project scoping, vendor negotiations, or team conflicts</li>
            <li>Do <strong>not</strong> go back and change responses once submitted</li>
            <li>Each question must be answered before you can proceed to the next</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "30px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📊 Understanding Your Results
          </h3>
          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            Once you complete the survey, your responses will generate scores across all five negotiating styles. Each style is scored independently out of <strong>25</strong>. Your highest score(s) indicate your dominant negotiating style.
          </p>

          <div style={{ borderTop: "1px solid #3498db", borderBottom: "1px solid #3498db", padding: "15px 0", margin: "20px 0", textAlign: "center" }}>
            <p style={{ fontStyle: "italic", color: "#3498db", margin: 0, fontWeight: "500", fontSize: "15px" }}>
              Your negotiating style reflects how you naturally balance getting what you need with preserving the relationship.
            </p>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "30px", fontSize: "14px", border: "2px solid #000" }}>
            <thead>
              <tr>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left", width: "25%" }}>Style</th>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left" }}>What It Means</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Competing (CO)</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  Results-oriented and assertive. You focus on the bottom line and are confident in pushing for your position. Effective in high-stakes situations, but can strain relationships if overused.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Collaborating (CL)</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  High assertiveness paired with high cooperativeness. You seek creative solutions that genuinely work for both parties. The gold standard for complex, long-term negotiations.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Compromising (CM)</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  You aim for the middle ground — give a little, get a little. Pragmatic and time-efficient, though it can leave value on the table that a collaborative approach would have captured.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Accommodating (AC)</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  You prioritise the relationship over your own outcome. Builds goodwill and trust, but chronic accommodation can lead to cumulative disadvantage in recurring negotiations.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Avoiding (AV)</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  You prefer to step back from conflict or defer decisions. Has a narrow legitimate use, but systematic avoidance leaves issues unresolved and cedes ground to others.
                </td>
              </tr>
            </tbody>
          </table>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            Most people use more than one style depending on the situation. Understanding your natural tendencies helps you negotiate more deliberately, adapt to different counterparts, and produce better outcomes.
          </p>
          <p style={{ color: "#000", fontSize: "15px" }}>When you're ready, click "<strong>Start Survey</strong>" below.</p>
          <div style={{ textAlign: "center", paddingBottom: "20px" }}>
            <button className="nav-btn" onClick={() => setInstructionsRead(true)} style={{ fontSize: "16px", padding: "12px 40px", fontWeight: "bold" }}>
              Start Survey
            </button>
          </div>
        </div>
      </div>
    )
  }
  if (!showSurvey && isType11 && !instructionsRead) {
    return (
      <div className="survey-wrapper">
        <div className="survey-card" style={{ textAlign: "left", maxWidth: "800px", margin: "0 auto", color: "#000", fontFamily: "Arial, sans-serif" }}>

          <h2 style={{ textAlign: "center", fontSize: "26px", fontWeight: "bold", marginBottom: "20px", color: "#000" }}>
            Welcome
          </h2>

          <div style={{ textAlign: "center", marginBottom: "25px" }}>
            <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "5px" }}>
              Locus of Control Inventory (LOCI)
            </div>
            <div style={{ fontStyle: "italic", fontSize: "15px" }}>
              Understanding What You Believe Drives Outcomes in Your Life and Work
            </div>
          </div>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "30px", textAlign: "left" }}>
            This inventory, based on <strong>Levenson's</strong> multidimensional locus of control framework, helps you understand what you believe controls outcomes in your life. It measures three independent orientations: <strong>Internal (I)</strong> — your own effort and decisions, <strong>External-Others (EO)</strong> — the influence of powerful people around you, and <strong>External-Chance (EC)</strong> — the role of luck, fate, and circumstances. It takes approximately <strong>8–10 minutes</strong> to complete.
          </p>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📋 What to Expect
          </h3>
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>You will respond to <strong>30 statements</strong> about what you believe causes things to happen in your professional and personal life</li>
            <li>For each statement, indicate how much you <strong>agree or disagree</strong> based on your genuine beliefs</li>
            <li>At the end, you will receive three independent scores — one for each orientation — along with a profile of your dominant attribution style</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ✅ How to Take the Survey
          </h3>
          <ol style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px" }}>
            <li><strong>Read each statement carefully.</strong> Reflect on what you genuinely believe — not what you think the ideal professional should believe.</li>
            <li><strong>Respond based on your actual beliefs.</strong> There is no correct answer. Your honest response is the most meaningful one.</li>
            <li><strong>Do not overthink.</strong> Your first instinct is usually the most accurate reflection of your true orientation.</li>
            <li><strong>Answer in order.</strong> Complete each question before moving to the next. Do not skip ahead or go back.</li>
            <li><strong>The three scales are independent.</strong> You can score high on more than one — this is normal and reflects a balanced or context-sensitive attribution style.</li>
          </ol>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            ⚡ A Few Important Reminders
          </h3>
          <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
            <li>Answer based on what you <strong>truly believe</strong> — not what you think will reflect well on you</li>
            <li>Think about your <strong>general tendencies</strong> across life and work, not just one specific situation</li>
            <li>Do <strong>not</strong> go back and change responses once submitted</li>
            <li>Each question must be answered before you can proceed to the next</li>
          </ul>

          <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "30px" }} />

          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
            📊 Understanding Your Results
          </h3>
          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            Once you complete the inventory, you will receive three independent scores — each ranging from <strong>10 to 40</strong>. Higher scores indicate stronger endorsement of that attribution style. Scores are interpreted as: <strong>Low (10–20) · Moderate (21–29) · High (30–40)</strong>.
          </p>

          <div style={{ borderTop: "1px solid #3498db", borderBottom: "1px solid #3498db", padding: "15px 0", margin: "20px 0", textAlign: "center" }}>
            <p style={{ fontStyle: "italic", color: "#3498db", margin: 0, fontWeight: "500", fontSize: "15px" }}>
              Your locus of control profile reflects what you believe is truly in your hands — and what you believe lies beyond them.
            </p>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "30px", fontSize: "14px", border: "2px solid #000" }}>
            <thead>
              <tr>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left", width: "25%" }}>Orientation</th>
                <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left" }}>What It Means</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Internal (I)</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  You believe your own actions, effort, and decisions are the primary drivers of outcomes. You take ownership, persist through obstacles, and rarely attribute results to forces outside your control.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>External-Others (EO)</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  You believe outcomes are significantly shaped by powerful people around you — bosses, senior leaders, and key decision-makers. You are highly attuned to hierarchy, relationships, and approval signals.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>External-Chance (EC)</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  You believe that luck, fate, timing, and unpredictable circumstances play a meaningful role in determining outcomes. You may feel that consistent effort does not always guarantee results.
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Balanced Profile</td>
                <td style={{ padding: "12px", border: "1px solid #000" }}>
                  You draw on multiple attribution styles depending on the situation. This reflects contextual judgment — the ability to adapt how you explain and respond to outcomes based on real-world complexity.
                </td>
              </tr>
            </tbody>
          </table>

          <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
            Understanding your locus of control helps you recognise where you invest energy, how you respond to setbacks, and where deliberate development can make the greatest impact on your professional effectiveness.
          </p>
          <p style={{ color: "#000", fontSize: "15px" }}>When you're ready, click "<strong>Start Survey</strong>" below.</p>
          <div style={{ textAlign: "center", paddingBottom: "20px" }}>
            <button className="nav-btn" onClick={() => setInstructionsRead(true)} style={{ fontSize: "16px", padding: "12px 40px", fontWeight: "bold" }}>
              Start Survey
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!showSurvey && isType12 && !instructionsRead) {
  return (
    <div className="survey-wrapper">
      <div className="survey-card" style={{ textAlign: "left", maxWidth: "800px", margin: "0 auto", color: "#000", fontFamily: "Arial, sans-serif" }}>

        <h2 style={{ textAlign: "center", fontSize: "26px", fontWeight: "bold", marginBottom: "20px", color: "#000" }}>
          Welcome
        </h2>

        <div style={{ textAlign: "center", marginBottom: "25px" }}>
          <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "5px" }}>
            Transition 90
          </div>
          <div style={{ fontStyle: "italic", fontSize: "15px" }}>
            First-Time Manager Assessment — Gauging Your Readiness for the Move Into Management
          </div>
        </div>

        <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "30px", textAlign: "left" }}>
          This assessment looks at what it really takes to succeed in your first 90 days as a manager. It measures two things: your <strong>mindset</strong> — the underlying beliefs and orientations that shape how you lead — and your <strong>situational skills</strong> — how you actually respond when real management moments show up. It takes approximately <strong>15–20 minutes</strong> to complete.
        </p>

        <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

        <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
          📋 What to Expect
        </h3>
        <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
          <li>You will respond to <strong>36 mindset statements</strong> about your beliefs and orientation toward leading others</li>
          <li>You will also work through <strong>24 situational skill questions</strong> — realistic scenarios where you choose how you'd actually respond</li>
          <li>Together, these map to <strong>12 mindset dimensions</strong> and <strong>12 skill dimensions</strong></li>
          <li>At the end, you will receive an overall readiness score, along with a breakdown across all 24 dimensions</li>
        </ul>

        <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

        <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
          ✅ How to Take the Assessment
        </h3>
        <ol style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px" }}>
          <li><strong>Read each statement or scenario carefully.</strong> Reflect on how you genuinely think and behave — not how you think an ideal manager should respond.</li>
          <li><strong>For mindset statements,</strong> indicate how much you agree or disagree based on your real beliefs.</li>
          <li><strong>For skill scenarios,</strong> choose the option that best reflects what you would actually do, not what sounds best.</li>
          <li><strong>Do not overthink.</strong> Your first instinct is usually the most accurate reflection of your true tendency.</li>
          <li><strong>Answer in order.</strong> Complete each question before moving to the next. Do not skip ahead or go back.</li>
        </ol>

        <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "20px" }} />

        <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
          ⚡ A Few Important Reminders
        </h3>
        <ul style={{ lineHeight: "1.8", paddingLeft: "40px", marginBottom: "30px", fontSize: "15px", listStyleType: "disc" }}>
          <li>Answer based on what you <strong>truly believe and would actually do</strong> — not what will reflect well on you</li>
          <li>Think about your <strong>general tendencies</strong> across your work, not just one specific situation</li>
          <li>Do <strong>not</strong> go back and change responses once submitted</li>
          <li>Each question must be answered before you can proceed to the next</li>
        </ul>

        <hr style={{ border: "0", height: "1px", background: "#ccc", marginBottom: "30px" }} />

        <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
          📊 Understanding Your Results
        </h3>
        <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
          Once you complete the assessment, you will receive an <strong>Overall Readiness</strong> score, plus separate <strong>Mindset</strong> and <strong>Skill</strong> scores, each broken down across 12 dimensions. Each dimension is rated as: <strong>Development Priority · Emerging Strength · Established Strength · Core Strength</strong>.
        </p>

        <div style={{ borderTop: "1px solid #c0392b", borderBottom: "1px solid #c0392b", padding: "15px 0", margin: "20px 0", textAlign: "center" }}>
          <p style={{ fontStyle: "italic", color: "#c0392b", margin: 0, fontWeight: "500", fontSize: "15px" }}>
            The first 90 days set the tone — this assessment shows you where you stand today, not where you're capped.
          </p>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "30px", fontSize: "14px", border: "2px solid #000" }}>
          <thead>
            <tr>
              <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left", width: "25%" }}>Area</th>
              <th style={{ padding: "12px", border: "2px solid #000", textAlign: "left" }}>What It Covers</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Mindset (12 dimensions)</td>
              <td style={{ padding: "12px", border: "1px solid #000" }}>
                Letting go of individual-contributor habits, handling power dynamics and insecurities, creating boundaries, holding people accountable, believing in yourself, and seeing the wider organisation as a system.
              </td>
            </tr>
            <tr>
              <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Skill (12 dimensions)</td>
              <td style={{ padding: "12px", border: "1px solid #000" }}>
                Delegation, giving and receiving feedback, building trust, being assertive, setting priorities, team planning, spotting talent, coaching, conducting reviews, team building, managing stakeholders, and influencing without formal authority.
              </td>
            </tr>
            <tr>
              <td style={{ padding: "12px", border: "1px solid #000", fontWeight: "bold" }}>Overall Readiness</td>
              <td style={{ padding: "12px", border: "1px solid #000" }}>
                A combined view of your mindset and skill scores, giving a single snapshot of how prepared you are for the transition into your first management role.
              </td>
            </tr>
          </tbody>
        </table>

        <p style={{ fontSize: "15px", lineHeight: "1.6", marginBottom: "20px" }}>
          Understanding your readiness profile helps you and your organisation prioritise coaching conversations on the areas that matter most before — and during — your first 90 days as a manager.
        </p>
        <p style={{ color: "#000", fontSize: "15px" }}>When you're ready, click "<strong>Start Assessment</strong>" below.</p>
        <div style={{ textAlign: "center", paddingBottom: "20px" }}>
          <button className="nav-btn" onClick={() => setInstructionsRead(true)} style={{ fontSize: "16px", padding: "12px 40px", fontWeight: "bold" }}>
            Start Assessment
          </button>
        </div>
      </div>
    </div>
  )
}
  //Make Instructiosn page for type8 and type9

  // ✅ STEP 2: DROPDOWNS & INITIAL FORM
  if (!showSurvey) {
    const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    const hasBasicDetails = selectedDepartment && selectedLevel;
    const hasExtraDetails =
      isValidFullName(name) &&
      email.trim() && isValidEmail(email) &&
      isValidPhoneNumber(phone) &&
      isValidFreeText(location) &&
      isValidFreeText(companyText) &&
      isValidFreeText(departmentText) &&
      isValidFreeText(levelText);

    // ✅ Simplify the button disable logic now that validation is gone
    const isStartDisabled = isType3 || isType4 || isType5 || isType6 || isType7 || isType8 || isType9 || isType10 || isType11 || isType12? !hasExtraDetails : !hasBasicDetails;

    const errorStyle = { color: "red", fontSize: "12px", marginTop: "4px", display: "block" };
    const requiredMark = <span style={{ color: "red" }}> *</span>;

    return (
      <div className="survey-wrapper">
        <div className="survey-card">
          <h2>Select Your Details</h2>
          {isType3 || isType4 || isType5 || isType6 || isType7 || isType8 || isType9 || isType10 || isType11 || isType12? (
            <>
              <div className="form-group">
                <label>Name{requiredMark}</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => {
                    const val = e.target.value;
                    setName(val);
                    if (val && !isValidFullName(val)) {
                      setNameError("Please enter your full name (first and last name).");
                    } else {
                      setNameError("");
                    }
                  }}
                  onBlur={() => {
                    if (!isValidFullName(name)) {
                      setNameError("Please enter your full name (first and last name).");
                    }
                  }}
                  placeholder="Enter your full name"
                />
                {nameError && <span style={errorStyle}>{nameError}</span>}
              </div>

              <div className="form-group">
                <label>Email Id{requiredMark}</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEmail(val);
                    if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
                      setEmailError("Please enter a valid email address.");
                    } else {
                      setEmailError("");
                    }
                  }}
                  onBlur={() => {
                    if (!email.trim() || !isValidEmail(email)) {
                      setEmailError("Please enter a valid email address.");
                    }
                  }}
                  placeholder="Enter your email"
                />
                {emailError && (
                  <span style={errorStyle}>
                    {emailError}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label>Phone no{requiredMark}</label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPhone(val);
                    if (val && !isValidPhoneNumber(val)) {
                      setPhoneError("Please enter a valid phone number (digits only, 10–12 digits).");
                    } else {
                      setPhoneError("");
                    }
                  }}
                  onBlur={() => {
                    if (!isValidPhoneNumber(phone)) {
                      setPhoneError("Please enter a valid phone number (digits only, 10–12 digits).");
                    }
                  }}
                  placeholder="Enter your phone number"
                />
                {phoneError && <span style={errorStyle}>{phoneError}</span>}
              </div>

              <div className="form-group">
                <label>Location{requiredMark}</label>
                <input
                  type="text"
                  required
                  value={location}
                  onChange={(e) => {
                    const val = e.target.value;
                    setLocation(val);
                    setLocationError(val && !isValidFreeText(val) ? "Please enter a valid location." : "");
                  }}
                  onBlur={() => {
                    if (!isValidFreeText(location)) setLocationError("Please enter a valid location.");
                  }}
                  placeholder="Enter your location"
                />
                {locationError && <span style={errorStyle}>{locationError}</span>}
              </div>

              <div className="form-group">
                <label>Organization{requiredMark}</label>
                <input
                  type="text"
                  required
                  value={companyText}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCompanyText(val);
                    setCompanyError(val && !isValidFreeText(val) ? "Please enter your company name." : "");
                  }}
                  onBlur={() => {
                    if (!isValidFreeText(companyText)) setCompanyError("Please enter your company name.");
                  }}
                  placeholder="Enter your company name"
                />
                {companyError && <span style={errorStyle}>{companyError}</span>}
              </div>

              <div className="form-group">
                <label>Department{requiredMark}</label>
                <input
                  type="text"
                  required
                  value={departmentText}
                  onChange={(e) => {
                    const val = e.target.value;
                    setDepartmentText(val);
                    setDepartmentError(val && !isValidFreeText(val) ? "Please enter your department." : "");
                  }}
                  onBlur={() => {
                    if (!isValidFreeText(departmentText)) setDepartmentError("Please enter your department.");
                  }}
                  placeholder="Enter your department"
                />
                {departmentError && <span style={errorStyle}>{departmentError}</span>}
              </div>

              <div className="form-group">
                <label>Designation{requiredMark}</label>
                <input
                  type="text"
                  required
                  value={levelText}
                  onChange={(e) => {
                    const val = e.target.value;
                    setLevelText(val);
                    setLevelError(val && !isValidFreeText(val) ? "Please enter your designation." : "");
                  }}
                  onBlur={() => {
                    if (!isValidFreeText(levelText)) setLevelError("Please enter your designation.");
                  }}
                  placeholder="Enter your level"
                />
                {levelError && <span style={errorStyle}>{levelError}</span>}
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label>Department{requiredMark}</label>
                <select required value={selectedDepartment} onChange={(e) => setSelectedDepartment(e.target.value)}>
                  <option value="">-- Select Department --</option>
                  {departments.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
                </select>
              </div>
              <div className="form-group">
                <label>Level{requiredMark}</label>
                <select required value={selectedLevel} onChange={(e) => setSelectedLevel(e.target.value)}>
                  <option value="">-- Select Level --</option>
                  {levels.map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
                </select>
              </div>
            </>
          )}
          <button
            className="nav-btn"
            disabled={isStartDisabled}
            onClick={() => { setCurrentGroupIndex(0); setCurrentQuestionPage(0); setShowSurvey(true); }}
          >
            Start Survey
          </button>
        </div>
      </div>
    );
  }

  const totalSurveyQuestions = survey?.groups?.flatMap(g => g.questions).length || 0;

  // ✅ Count how many questions are answered (same rules as allAnsweredCurrent)
  const isQuestionAnswered = (q) => {
    const ans = answers[q.id];
    if (q.ans_type === "textbox") return isValidText(ans);
    if (q.ans_type === "dual_textbox") {
      const [field1, field2, disable1, disable2] = Array.isArray(ans)
        ? ans : ["", "", false, false];
      if (!disable1 && !isValidText(field1)) return false;
      if (!disable2 && !isValidText(field2)) return false;
      return true;
    }
    // Navigating Conflict table: answer object { selected_answer, who_do_this }
    if (ans && typeof ans === "object" && !Array.isArray(ans) && "selected_answer" in ans) {
      return !!ans.selected_answer;
    }
    // Johari Window: answer object { [letterA]: valueA, [letterB]: valueB }
    if (q.ans_type === "mcq" && isType7) {
      return !!(ans && typeof ans === "object" && !Array.isArray(ans) && Object.keys(ans).length === 2);
    }
    return !!(ans && ans.length > 0);
  };

  const answeredSurveyQuestions =
    (survey?.groups?.flatMap(g => g.questions || []) || []).filter(isQuestionAnswered).length;

  // ✅ STEP 3: SURVEY UI FINALLY RENDERED
  return (
    <div className="survey-wrapper">
      <div className="survey-card">
        <div className="survey-header">
          <h1>{survey?.survey_name}</h1>
          <p className="company-name">by {survey?.company_name}</p>
        </div>

        {errorMessage && <div className="error-banner">{errorMessage}</div>}

        <form onSubmit={handleSubmit} className={isType3 ? "type3-align-left" : ""}>
          <h2 className="survey-group-title">{currentGroup?.group}</h2>

          {/* ─────────────────────────────────────────────────────────────
               NAVIGATING CONFLICT — table: row = behaviour,
               cols = Not Acceptable | Tolerable | Perfectly Acceptable | # Who Do This
          ───────────────────────────────────────────────────────────── */}
          {isType2 && currentGroupName === "Navigating Conflict" ? (
            <div className="navigating-conflict-table-wrapper" style={{ overflowX: "auto", marginBottom: "24px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                <thead>
                  <tr style={{ background: "#f0f4ff" }}>
                    <th style={{ padding: "10px 14px", textAlign: "left", border: "1px solid #d0d5e8", minWidth: "220px" }}>Behaviour</th>
                    {["Not Acceptable", "Tolerable", "Perfectly Acceptable"].map((col) => (
                      <th key={col} style={{ padding: "10px 14px", textAlign: "center", border: "1px solid #d0d5e8", whiteSpace: "nowrap" }}>{col}</th>
                    ))}
                    <th style={{ padding: "10px 14px", textAlign: "center", border: "1px solid #d0d5e8", whiteSpace: "nowrap" }}># Who Do This</th>
                  </tr>
                </thead>
                <tbody>
                  {currentQuestions.map((q, idx) => {
                    const ans = answers[q.id] || { selected_answer: null, who_do_this: false };
                    return (
                      <tr key={q.id} style={{ background: idx % 2 === 0 ? "#fff" : "#f9fafc" }}>
                        <td style={{ padding: "10px 14px", border: "1px solid #d0d5e8" }}>{q.question}</td>
                        {["Not Acceptable", "Tolerable", "Perfectly Acceptable"].map((opt) => (
                          <td key={opt} style={{ padding: "10px 14px", textAlign: "center", border: "1px solid #d0d5e8" }}>
                            <input
                              type="radio"
                              name={`nav-conflict-${q.id}`}
                              value={opt}
                              checked={ans.selected_answer === opt}
                              onChange={() => handleNavigatingConflictChange(q.id, "selected_answer", opt)}
                              style={{ cursor: "pointer", width: "18px", height: "18px" }}
                            />
                          </td>
                        ))}
                        <td style={{ padding: "10px 14px", textAlign: "center", border: "1px solid #d0d5e8" }}>
                          <input
                            type="checkbox"
                            checked={!!ans.who_do_this}
                            onChange={(e) => handleNavigatingConflictChange(q.id, "who_do_this", e.target.checked)}
                            style={{ cursor: "pointer", width: "18px", height: "18px" }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            /* ─────────────────────────────────────────────────────────────
                 TEAM CULTURE MULTISELECT groups:
                 Building Trust | Committing to Decisions |
                 Embracing Accountability | Focusing on Results
            ───────────────────────────────────────────────────────────── */
          ) : isType2 && currentGroup?.questions?.[0]?.is_team_culture ? (
            <div>
              {currentQuestions.map((q) => {
                const selectedArr = Array.isArray(answers[q.id]) ? answers[q.id] : [];
                return (
                  <div key={q.id} style={{ marginBottom: "28px" }}>
                    <p style={{ fontWeight: "600", marginBottom: "12px", fontSize: "15px" }}>{q.question}</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {(q.mcq_options || []).map((opt) => {
                        const checked = selectedArr.includes(opt);
                        return (
                          <label
                            key={opt}
                            style={{
                              display: "flex", alignItems: "flex-start", gap: "10px",
                              cursor: "pointer", padding: "10px 14px",
                              border: checked ? "2px solid #3b82f6" : "1px solid #d0d5e8",
                              borderRadius: "8px",
                              background: checked ? "#eff6ff" : "#fff",
                              transition: "all 0.15s",
                            }}
                          >
                            <input
                              type="checkbox"
                              value={opt}
                              checked={checked}
                              onChange={(e) => handleAnswerChange(q.id, opt, e.target.checked)}
                              style={{ marginTop: "2px", width: "16px", height: "16px", flexShrink: 0 }}
                            />
                            <span style={{ fontSize: "14px", lineHeight: "1.5" }}>{opt}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            /* ─────────────────────────────────────────────────────────────
                 JOHARI WINDOW (survey_type_fk = 7) — each MCQ question
                 has exactly 2 options. Instead of picking one option,
                 the respondent splits 5 points across both options via
                 two linked dropdowns (Option A + Option B always sum to 5).
            ───────────────────────────────────────────────────────────── */
          ) : isType7 ? (
            <div>
              {currentQuestions.map((q) => {
                const opts = Array.isArray(q.mcq_options) ? q.mcq_options : [];
                const [optA, optB] = opts;
                const letterA = getOptionLetter(optA);
                const letterB = getOptionLetter(optB);
                const ans = answers[q.id] || {};
                const valueA = ans[letterA] ?? "";
                const valueB = ans[letterB] ?? "";

                return (
                  <div
                    key={q.id}
                    style={{
                      marginBottom: "20px",
                      padding: "16px 18px",
                      border: "1px solid #d0d5e8",
                      borderRadius: "10px",
                      background: "#fff",
                      boxSizing: "border-box",
                    }}
                  >
                    <p style={{ fontWeight: 600, marginBottom: "14px", fontSize: "15px" }}>
                      {q.question_no != null ? `${q.question_no}. ` : ""}
                      {q.question}
                    </p>

                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {[
                        { letter: letterA, other: letterB, opt: optA, value: valueA },
                        { letter: letterB, other: letterA, opt: optB, value: valueB },
                      ].map(({ letter, other, opt, value }) => (
                        <div
                          key={letter}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "12px",
                            flexWrap: "wrap",
                            padding: "10px 14px",
                            border: value !== "" ? "2px solid #3b82f6" : "1px solid #d0d5e8",
                            borderRadius: "8px",
                            background: value !== "" ? "#eff6ff" : "#fff",
                            transition: "all 0.15s",
                            boxSizing: "border-box",
                            width: "100%",
                          }}
                        >
                          <span style={{ fontSize: "14px", lineHeight: "1.5", flex: 1, minWidth: "200px" }}>
                            {stripOptionLabel(opt)}
                          </span>
                          <select
                            value={value}
                            onChange={(e) => handleJHScoreChange(q.id, letterA, letterB, letter, e.target.value)}
                            style={{
                              padding: "6px 10px",
                              borderRadius: "6px",
                              border: "1px solid #d0d5e8",
                              minWidth: "72px",
                              fontSize: "14px",
                              cursor: "pointer",
                              background: "#fff",
                            }}
                          >
                            <option value="">--</option>
                            {[0, 1, 2, 3, 4, 5].map((n) => (
                              <option key={n} value={n}>{n}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>

                    <p style={{ fontSize: "12px", color: "#7a7f95", marginTop: "10px" }}>
                      Split 5 points between the two statements above based on how much each reflects you.
                    </p>
                  </div>
                );
              })}
            </div>

            /* ─────────────────────────────────────────────────────────────
                 DEFAULT — existing QuestionRenderer (all other cases)
            ───────────────────────────────────────────────────────────── */
            // ) : (
            //   currentQuestions.map((q) => {
            //     const formattedQuestionText = (isType3 && q.question_no != null)
            //       ? `${q.question_no}. ${q.question}`
            //       : q.question;
            //     const modifiedQuestion = { ...q, question: formattedQuestionText };
            //     return (
            //       <div key={q.id} className={isType3 ? "type3-question-wrapper" : ""}>
            //         <QuestionRenderer
            //           key={q.id}
            //           question={modifiedQuestion}
            //           selectedAnswer={answers[q.id]}
            //           onAnswerChange={handleAnswerChange}
            //         />
            //       </div>
            //     );
            //   })
            // )}

          ) : (
            currentQuestions.map((q) => {
              const formattedQuestionText = (isType3 && q.question_no != null)
                ? `${q.question_no}. ${q.question}`
                : q.question;

              // Build a map of stripped → original for remapping stored answers
              const isIndividualType = isType3 || isType4 || isType5 || isType6 || isType7 || isType8 || isType9 || isType10 || isType11 || isType12;
              const optionMap = {};
              if (isIndividualType && Array.isArray(q.mcq_options)) {
                q.mcq_options.forEach(opt => {
                  optionMap[stripOptionLabel(opt)] = opt;
                });
              }

              const modifiedQuestion = {
                ...q,
                question: formattedQuestionText,
                // Strip A. B. C. for display only, original values kept in optionMap
                mcq_options: isIndividualType && Array.isArray(q.mcq_options)
                  ? q.mcq_options.map(stripOptionLabel)
                  : q.mcq_options,
              };

              return (
                <div key={q.id} className={isType3 || isType6 ? "type3-question-wrapper" : ""}>
                  <QuestionRenderer
                    key={q.id}
                    question={modifiedQuestion}
                    // Map stored original answer → stripped so QuestionRenderer shows correct selected state
                    selectedAnswer={
                      isIndividualType && answers[q.id]
                        ? stripOptionLabel(answers[q.id])
                        : answers[q.id]
                    }
                    onAnswerChange={(qId, strippedValue, checked) => {
                      // Remap stripped value back to original before storing
                      const originalValue = optionMap[strippedValue] ?? strippedValue;
                      handleAnswerChange(qId, originalValue, checked);
                    }}
                  />
                </div>
              );
            })
          )}

          <div className="pagination-controls">
            {(currentGroupIndex > 0 || currentQuestionPage > 0) && (
              <button className="nav-btn" type="button" onClick={handlePrev}>← Previous</button>
            )}

            {!isLastGroup ? (
              <button className="nav-btn" type="button" disabled={!allAnsweredCurrent} onClick={handleNext}>Next →</button>
            ) : (
              <button type="submit" disabled={!allAnsweredCurrent}>Submit Survey</button>
            )}
          </div>

          <div className="page-indicator">
            {`${answeredSurveyQuestions} of ${totalSurveyQuestions} questions answered`}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SurveyPage;